/* Brightivity welcome — the desktop's sign-in gate (LicenseModal + LoginModal + ConnectSignupModal), on
 * brightivity.dev/auth (Brightivity's own code, shared accounts): Sign in · My school, district or company paid
 * (8-letter key, or ask for it: /auth/signup-request — pending-signups.md) · Just me, free (Connect).
 * Email code, or Google / Microsoft / Apple once Brightivity's own sign-in apps are registered. */
import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { api } from '../api/client.js';
import { API_BASE } from '../config.js';
import { useSession } from '../app/session.jsx';
import { Button, Card, Icon, Input, SearchInput, Select, Spinner, Tag, Textarea } from '../ui/index.jsx';
import { COUNTRY_OPTIONS } from '../features/auth/countryOptions.js';
import '../app/shell.css';
import '../features/auth/auth.css';
import { Trans, useTranslation } from 'react-i18next';
import i18n from '../i18n/index.js';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const store = {
  get: (k) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k, v) => { try { localStorage.setItem(k, v); } catch { /* blocked */ } },
  del: (k) => { try { localStorage.removeItem(k); } catch { /* blocked */ } },
};
const PROVIDERS = [{ id: 'google', label: 'Google' }, { id: 'microsoft', label: 'Microsoft' }, { id: 'apple', label: 'Apple' }];

function ProviderIcon({ id }) {
  if (id === 'google') return <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" /><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" /><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" /><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" /></svg>;
  if (id === 'microsoft') return <svg width="20" height="20" viewBox="0 0 23 23" aria-hidden="true"><path fill="#F25022" d="M1 1h10v10H1z" /><path fill="#7FBA00" d="M12 1h10v10H12z" /><path fill="#00A4EF" d="M1 12h10v10H1z" /><path fill="#FFB900" d="M12 12h10v10H12z" /></svg>;
  return <svg width="18" height="20" viewBox="0 0 17 20" aria-hidden="true"><path fill="#000" d="M14.1 10.6c0-2.6 2.1-3.8 2.2-3.9-1.2-1.8-3.1-2-3.7-2-1.6-.2-3.1.9-3.9.9-.8 0-2.1-.9-3.4-.9C3.5 4.7 1.9 5.7 1 7.3c-1.9 3.2-.5 8 1.3 10.6.9 1.3 1.9 2.7 3.3 2.6 1.3-.1 1.8-.8 3.4-.8 1.6 0 2 .8 3.4.8 1.4 0 2.3-1.3 3.2-2.6 1-1.5 1.4-2.9 1.4-3-.1 0-2.9-1.1-2.9-4.3zM11.5 3c.7-.9 1.2-2 1.1-3.2-1 0-2.3.7-3 1.6-.7.8-1.3 2-1.1 3.1 1.1.1 2.3-.6 3-1.5z" /></svg>;
}

/** Google / Microsoft / Apple: go to the provider through brightivity.dev; it comes back to /auth/callback. */
async function startSso(p, { licenseId, signupToken } = {}) {
  const qs = new URLSearchParams({ return_to: window.location.origin });
  if (licenseId) qs.set('license_id', licenseId);
  if (signupToken) qs.set('signup_token', signupToken);
  const r = await api(`/auth/${p}/start?${qs.toString()}`, { auth: false });
  if (!r.ok) return r.error || i18n.t('bv:auth.ssoFailed', { provider: p });
  store.set('bv.lastMethod', p);
  window.location.assign(r.data.authUrl);
  return null;
}

function SsoRow({ providers, label, onPick, busy }) {
  const { t } = useTranslation('bv');
  return (
    <div className="bv-sso">
      <div className="bv-sso__or"><span>{label}</span></div>
      <div className="bv-sso__row">
        {PROVIDERS.map((p) => (
          <button key={p.id} type="button" className={`bv-sso__btn${providers && providers[p.id] === false ? ' is-off' : ''}`} disabled={busy}
            title={providers && providers[p.id] === false ? t('auth.ssoOff', { provider: p.label }) : t('auth.continueWith', { provider: p.label })} onClick={() => onPick(p.id)} data-testid={`sso-${p.id}`}>
            <ProviderIcon id={p.id} /><span>{p.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ views */
function Choose({ go, reason }) {
  const { t } = useTranslation('bv');
  return (
    <>
      <h1 className="bv-signin__title">{t('auth.welcome')}</h1>
      <p className="bv-signin__sub">{t('auth.chooseHow')}</p>
      {reason && <p className="bv-signin__msg bv-signin__msg--err" role="alert" data-testid="logout-reason">{reason}</p>}
      <div className="bv-gate" data-testid="auth-gate">
        <button type="button" className="bv-gate__card" onClick={() => go('signin')} data-testid="gate-signin">
          <span className="bv-icontile bv-icontile--blue"><Icon name="arrow" size={26} /></span>
          <span><b>{t('auth.gate.signin')}</b><span>{t('auth.gate.signinSub')}</span></span>
        </button>
        <button type="button" className="bv-gate__card" onClick={() => go('printer')} data-testid="gate-org">
          <span className="bv-icontile bv-icontile--purple"><Icon name="school" size={26} /></span>
          <span><b>{t('auth.gate.org')}</b><span>{t('auth.gate.orgSub')}</span></span>
        </button>
        <button type="button" className="bv-gate__card" onClick={() => go('signup')} data-testid="gate-signup">
          <span className="bv-icontile bv-icontile--green"><Icon name="sparkle" size={26} /></span>
          <span><b>{t('auth.gate.signup')}</b><span>{t('auth.gate.signupSub')}</span></span>
        </button>
      </div>
    </>
  );
}

/** Card 2, first question: does your organization have a plan with Poster Studio Express? Yes → the key question, No → free account. */
function PrinterAsk({ go }) {
  const { t } = useTranslation('bv');
  return (
    <>
      <h1 className="bv-signin__title">{t('auth.printer.title')}</h1>
      <p className="bv-signin__sub">{t('auth.printer.sub')}</p>
      <div className="bv-orgask" data-testid="printer-ask">
        <Button variant="coral" block icon="check" style={{ height: 56 }} onClick={() => go('org')} data-testid="printer-yes">{t('auth.printer.yes')}</Button>
        <Button block iconRight="arrow" style={{ height: 56 }} onClick={() => go('signup')} data-testid="printer-no">{t('auth.printer.no')}</Button>
      </div>
      <div className="bv-signin__alt"><button type="button" onClick={() => go('choose')}>{t('common.back')}</button></div>
    </>
  );
}

/** Card 2, second question: "Do you have your 8-letter key?" — Yes → License, No → the key request form. */
function OrgAsk({ go }) {
  const { t } = useTranslation('bv');
  return (
    <>
      <h1 className="bv-signin__title">{t('auth.org.title')}</h1>
      <p className="bv-signin__sub">{t('auth.org.sub')}</p>
      <div className="bv-orgask" data-testid="org-ask">
        <Button variant="coral" block icon="check" style={{ height: 56 }} onClick={() => go('license')} data-testid="org-yes">{t('auth.org.yes')}</Button>
        <Button block iconRight="arrow" style={{ height: 56 }} onClick={() => go('request')} data-testid="org-no">{t('auth.org.no')}</Button>
      </div>
      <div className="bv-signin__alt"><button type="button" onClick={() => go('printer')}>{t('common.back')}</button></div>
    </>
  );
}

const ORG_TYPE = (o) => (o.kind === 'district' ? 'district' : o.type === 'college' ? 'college' : o.type === 'company' ? 'company' : 'school');
function orgPlace(o) { return [o.city, o.state].filter(Boolean).join(', '); }

/** Organization search for the key request: GET /auth/org-search?q= (≥ 2 chars), debounced. */
function OrgSearch({ value, onPick }) {
  const { t } = useTranslation('bv');
  const [q, setQ] = useState('');
  const [res, setRes] = useState(null); // null = not searched; [] = no match
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const s = q.trim();
    if (s.length < 2) { setRes(null); setBusy(false); return undefined; }
    setBusy(true);
    const ctl = new AbortController();
    const h = setTimeout(async () => {
      const r = await api(`/auth/org-search?q=${encodeURIComponent(s)}`, { auth: false, signal: ctl.signal });
      if (ctl.signal.aborted) return;
      setBusy(false);
      setRes(r.ok && r.data && Array.isArray(r.data.results) ? r.data.results : []);
    }, 300);
    return () => { clearTimeout(h); ctl.abort(); };
  }, [q]);
  if (value) {
    return (
      <div className="bv-orgpick__picked" data-testid="org-picked">
        <span className="bv-icontile bv-icontile--purple"><Icon name="school" size={20} /></span>
        <span className="bv-orgpick__text"><b>{value.name}</b><span>{t(`auth.rq.type.${ORG_TYPE(value)}`)}{orgPlace(value) ? ` · ${orgPlace(value)}` : ''}</span></span>
        <button type="button" className="bv-orgpick__change" onClick={() => { onPick(null); setQ(''); }} data-testid="org-change">{t('auth.rq.change')}</button>
      </div>
    );
  }
  return (
    <div className="bv-orgpick">
      <SearchInput id="rq-org-q" aria-label={t('auth.rq.org')} placeholder={t('auth.rq.search')} autoComplete="off" value={q} onChange={(e) => setQ(e.target.value)} />
      {busy && <p className="bv-small bv-muted" role="status">{t('auth.rq.searching')}</p>}
      {!busy && res && res.length === 0 && <p className="bv-small bv-muted" data-testid="org-none">{t('auth.rq.noResults')}</p>}
      {!busy && res && res.length > 0 && (
        <ul className="bv-orgpick__list" data-testid="org-results">
          {res.map((o) => (
            <li key={`${o.kind}-${o.id}`}>
              <button type="button" className="bv-orgpick__item" onClick={() => onPick(o)} data-testid="org-result">
                <b>{o.name}</b><span>{t(`auth.rq.type.${ORG_TYPE(o)}`)}{orgPlace(o) ? ` · ${orgPlace(o)}` : ''}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** No key yet: ask for it. POST /auth/signup-request → the owner sends the key by email (claim link). */
function KeyRequest({ go }) {
  const { t } = useTranslation('bv');
  const [f, setF] = useState({ name: '', email: '', orgName: '', orgCity: '', orgRegion: '', note: '' });
  const [org, setOrg] = useState(null);
  const [cantFind, setCantFind] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [sentTo, setSentTo] = useState(null);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    const email = f.email.trim().toLowerCase();
    if (!f.name.trim()) { setMsg(t('auth.rq.needName')); return; }
    if (!EMAIL_RE.test(email)) { setMsg(t('auth.enterEmail')); return; }
    if (cantFind ? !(f.orgName.trim() && f.orgCity.trim() && f.orgRegion.trim()) : !org) { setMsg(cantFind ? t('auth.rq.needTyped') : t('auth.rq.needOrg')); return; }
    const body = { name: f.name.trim(), email };
    if (cantFind) Object.assign(body, { orgTypedName: f.orgName.trim(), orgTypedCity: f.orgCity.trim(), orgTypedRegion: f.orgRegion.trim() });
    else if (org.kind === 'district') body.orgDistrictId = org.id;
    else body.orgSchoolId = org.id;
    if (f.note.trim()) body.note = f.note.trim();
    setBusy(true); setMsg(null);
    const r = await api('/auth/signup-request', { method: 'POST', body, auth: false });
    setBusy(false);
    if (!r.ok || !r.data || !r.data.success) { setMsg((r.data && (r.data.message || r.data.error)) || r.error || t('auth.rq.failed')); return; }
    setSentTo(email);
  };
  if (sentTo) {
    return (
      <div className="bv-signup-done" data-testid="request-done">
        <span className="bv-icontile bv-icontile--green" style={{ margin: '0 auto' }}><Icon name="check" size={30} /></span>
        <h1 className="bv-signin__title">{t('auth.rq.doneTitle')}</h1>
        <p className="bv-signin__sub" data-testid="request-done-text">{t('auth.rq.done', { email: sentTo })}</p>
        <Button variant="coral" block style={{ height: 52, marginTop: 12 }} onClick={() => go('choose')}>{t('auth.rq.backStart')}</Button>
      </div>
    );
  }
  return (
    <>
      <h1 className="bv-signin__title">{t('auth.rq.title')}</h1>
      <p className="bv-signin__sub">{t('auth.rq.sub')}</p>
      <form onSubmit={submit} noValidate className="bv-signup" data-testid="request-form">
        <label className="bv-label" htmlFor="rq-name">{t('auth.su.fullName')}</label>
        <Input id="rq-name" autoComplete="name" placeholder={t('auth.su.namePlaceholder')} value={f.name} onChange={set('name')} autoFocus />
        <label className="bv-label" htmlFor="rq-email">{t('auth.email')}</label>
        <Input id="rq-email" type="email" autoComplete="email" placeholder="you@school.org" value={f.email} onChange={set('email')} />
        <label className="bv-label" htmlFor={cantFind ? 'rq-org-name' : 'rq-org-q'}>{t('auth.rq.org')}</label>
        {!cantFind && <OrgSearch value={org} onPick={(o) => { setOrg(o); setMsg(null); }} />}
        <label className="bv-check"><input type="checkbox" checked={cantFind} onChange={(e) => { setCantFind(e.target.checked); if (e.target.checked) setOrg(null); setMsg(null); }} data-testid="rq-cantfind" /><span>{t('auth.rq.cantFind')}</span></label>
        {cantFind && (
          <>
            <Input id="rq-org-name" aria-label={t('auth.rq.orgName')} placeholder={t('auth.rq.orgName')} value={f.orgName} onChange={set('orgName')} autoFocus />
            <div className="bv-signup__row">
              <Input id="rq-org-city" aria-label={t('auth.su.city')} placeholder={t('auth.su.city')} value={f.orgCity} onChange={set('orgCity')} />
              <Input id="rq-org-region" aria-label={t('auth.rq.region')} placeholder={t('auth.rq.region')} value={f.orgRegion} onChange={set('orgRegion')} />
            </div>
          </>
        )}
        <label className="bv-label" htmlFor="rq-note">{t('auth.rq.note')}</label>
        <Textarea id="rq-note" rows={3} maxLength={500} placeholder={t('auth.rq.notePlaceholder')} value={f.note} onChange={set('note')} />
        <Button type="submit" variant="coral" block disabled={busy} style={{ marginTop: 12, height: 52 }}>{busy ? t('print.card.sending') : t('auth.rq.send')}</Button>
      </form>
      {msg && <p className="bv-signin__msg bv-signin__msg--err" role="alert" data-testid="request-msg">{msg}</p>}
      <div className="bv-signin__alt"><button type="button" onClick={() => go('org')}>{t('common.back')}</button></div>
    </>
  );
}

function License({ go, onValid }) {
  const { t } = useTranslation('bv');
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [lic, setLic] = useState(null);
  const check = async (e) => {
    if (e) e.preventDefault();
    if (key.length !== 8) { setErr(t('auth.lic.eight')); return; }
    setBusy(true); setErr(null);
    const r = await api('/auth/validate-license', { method: 'POST', body: { key }, auth: false });
    setBusy(false);
    if (!r.ok || !r.data || !r.data.valid) { setErr((r.data && r.data.error) || r.error || t('auth.lic.invalid')); return; }
    setLic(r.data.license);
  };
  return (
    <>
      <h1 className="bv-signin__title">{t('auth.lic.title')}</h1>
      <p className="bv-signin__sub">{t('auth.licenseSub')}</p>
      {!lic ? (
        <form onSubmit={check} noValidate data-testid="license-form">
          <label className="bv-label" htmlFor="lic-key">{t('auth.lic.label')}</label>
          <Input id="lic-key" className="bv-otp bv-lickey" value={key} maxLength={8} autoFocus autoComplete="off" placeholder="ABCDEFGH"
            onChange={(e) => setKey(e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 8))} />
          <p className="bv-small bv-muted bv-lickey__count">{t('auth.lic.count', { n: key.length })}</p>
          <Button type="submit" variant="coral" block disabled={busy || key.length !== 8} style={{ height: 52 }}>{busy ? t('print.checking') : t('auth.lic.check')}</Button>
        </form>
      ) : (
        <div className="bv-licok" data-testid="license-ok">
          <div className="bv-licok__head"><Icon name="check" size={22} /> {t('auth.lic.valid')}</div>
          {lic.is_master_admin ? <p><b>{t('auth.lic.master')}</b><br /><span className="bv-muted">{t('auth.lic.masterSub')}</span></p>
            : <p><b>{lic.school_name || t('school.yourSchool')}</b>{lic.school_state ? <span className="bv-muted"> · {lic.school_state}</span> : null}<br /><Tag color="purple">{t('auth.lic.plan', { tier: lic.tier || t('auth.lic.schoolTier') })}</Tag></p>}
          <Button variant="coral" block style={{ height: 52 }} onClick={() => onValid(lic)} data-testid="license-continue">{t('auth.lic.continue')}</Button>
        </div>
      )}
      {err && <p className="bv-signin__msg bv-signin__msg--err" role="alert">{err}</p>}
      <p className="bv-small bv-muted bv-center">{t('auth.lic.need')}</p>
      <div className="bv-signin__alt"><button type="button" onClick={() => go('org')}>{t('common.back')}</button></div>
    </>
  );
}

function SignIn({ go, license, presetEmail, providers, back }) {
  const { t } = useTranslation('bv');
  const { signIn } = useSession();
  const navigate = useNavigate();
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState(() => presetEmail || store.get('bv.lastEmail') || '');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const last = store.get('bv.lastMethod');
  const send = async (e) => {
    if (e) e.preventDefault();
    const em = email.trim().toLowerCase();
    if (!EMAIL_RE.test(em)) { setMsg({ err: true, text: t('auth.enterEmail') }); return; }
    setBusy(true); setMsg(null);
    const r = await api('/auth/email-otp/send', { method: 'POST', body: { email: em, ...(license ? { licenseId: license.id } : {}) }, auth: false });
    setBusy(false);
    if (!r.ok) { setMsg({ err: true, text: r.error }); return; }
    store.set('bv.lastEmail', em);
    setStep('code'); setCode('');
    setMsg({ ok: true, text: t('auth.codeSent', { email: em }) });
  };
  const verify = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) { setMsg({ err: true, text: t('auth.codeSix') }); return; }
    setBusy(true); setMsg(null);
    const r = await api('/auth/email-otp/verify', { method: 'POST', body: { email: email.trim().toLowerCase(), code, ...(license ? { licenseId: license.id } : {}) }, auth: false });
    setBusy(false);
    if (!r.ok || !r.data || !r.data.token) { setMsg({ err: true, text: r.error || t('auth.signinFailed') }); return; }
    store.set('bv.lastMethod', 'email');
    signIn(r.data.token, r.data.user);
    navigate(r.data.onboardingCompleted === false ? '/onboarding' : back, { replace: true });
  };
  const sso = async (p) => { setBusy(true); const e = await startSso(p, { licenseId: license && license.id }); setBusy(false); if (e) setMsg({ err: true, text: e }); };
  return (
    <>
      <h1 className="bv-signin__title">{step === 'email' ? t('auth.signIn') : t('auth.checkEmail')}</h1>
      <p className="bv-signin__sub">{step === 'email' ? (license ? t('auth.withLicense', { school: license.school_name || t('auth.lic.schoolTier').toLowerCase() }) : t('auth.emailCodeSub')) : t('auth.enterCodeSub')}</p>
      {step === 'email' ? (
        <>
          <form onSubmit={send} noValidate data-testid="signin-email">
            <label className="bv-label" htmlFor="si-email">{t('auth.email')}</label>
            <Input id="si-email" type="email" autoComplete="email" placeholder="you@school.org" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
            <Button type="submit" variant="coral" block disabled={busy} style={{ marginTop: 10, height: 52 }}>{busy ? t('print.card.sending') : t('auth.emailMeCode')}</Button>
          </form>
          <SsoRow providers={providers} label={t('auth.orSignInWith')} onPick={sso} busy={busy} />
          {last && last !== 'email' && <p className="bv-small bv-muted bv-center">{t('auth.usually', { provider: last[0].toUpperCase() + last.slice(1) })}</p>}
        </>
      ) : (
        <form onSubmit={verify} noValidate data-testid="signin-code">
          <label className="bv-label" htmlFor="si-code">{t('auth.sixDigit')}</label>
          <Input id="si-code" className="bv-otp" inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="000000" value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} autoFocus />
          <Button type="submit" variant="coral" block disabled={busy} style={{ marginTop: 10, height: 52 }}>{busy ? t('auth.signingIn') : t('auth.signIn')}</Button>
          <div className="bv-signin__alt">
            <button type="button" onClick={() => send()}>{t('auth.resend')}</button>{' · '}
            <button type="button" onClick={() => { setStep('email'); setMsg(null); }}>{t('auth.differentEmail')}</button>
          </div>
        </form>
      )}
      <p className={`bv-signin__msg ${msg ? (msg.err ? 'bv-signin__msg--err' : 'bv-signin__msg--ok') : ''}`} role={msg && msg.err ? 'alert' : 'status'} data-testid="signin-msg">{msg ? msg.text : ''}</p>
      {step === 'email' && <div className="bv-signin__alt"><button type="button" onClick={() => go('choose')}>{t('common.back')}</button></div>}
    </>
  );
}

function SignUp({ go, providers, onExists }) {
  const { t } = useTranslation('bv');
  const { signIn } = useSession();
  const navigate = useNavigate();
  const [step, setStep] = useState('profile');
  const [f, setF] = useState({ name: '', email: '', account_type: 'personal', organization_name: '', address_street: '', address_city: '', address_region: '', address_postal_code: '', address_country: 'US' });
  const [init, setInit] = useState(null); // {signupToken, licenseId, sig}
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const sig = JSON.stringify(f);
  const valid = f.name.trim() && EMAIL_RE.test(f.email.trim()) && (f.account_type === 'personal' || f.organization_name.trim()) && f.address_street.trim() && f.address_city.trim() && f.address_region.trim() && f.address_postal_code.trim() && f.address_country;
  const next = async (e) => {
    e.preventDefault();
    if (!valid) { setMsg({ err: true, text: t('auth.su.allFields') }); return; }
    if (init && init.sig === sig) { setStep('method'); setMsg(null); return; } // nothing changed: no second license
    setBusy(true); setMsg(null);
    const r = await api('/auth/signup/init', { method: 'POST', body: { ...f, email: f.email.trim().toLowerCase(), client: 'brightivity' }, auth: false });
    setBusy(false);
    if (!r.ok) {
      if (r.status === 409) { onExists(f.email.trim().toLowerCase()); return; }
      setMsg({ err: true, text: (r.data && r.data.message) || r.error || t('auth.su.failed') }); return;
    }
    setInit({ signupToken: r.data.signupToken, licenseId: r.data.licenseId, sig });
    setStep('method');
  };
  const sendCode = async () => {
    setBusy(true); setMsg(null);
    const r = await api('/auth/email-otp/send', { method: 'POST', body: { email: f.email.trim().toLowerCase(), licenseId: init.licenseId, signupToken: init.signupToken }, auth: false });
    setBusy(false);
    if (!r.ok) { setMsg({ err: true, text: r.error || t('auth.su.sendFailed') }); return; }
    setStep('otp'); setCode(''); setMsg({ ok: true, text: t('auth.codeSent', { email: f.email.trim().toLowerCase() }) });
  };
  const verify = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) { setMsg({ err: true, text: t('auth.codeSix') }); return; }
    setBusy(true); setMsg(null);
    const r = await api('/auth/email-otp/verify', { method: 'POST', body: { email: f.email.trim().toLowerCase(), code, licenseId: init.licenseId, signupToken: init.signupToken }, auth: false });
    setBusy(false);
    if (!r.ok || !r.data || !r.data.token) { setMsg({ err: true, text: r.error || t('auth.su.badCode') }); return; }
    store.set('bv.lastMethod', 'email'); store.set('bv.lastEmail', f.email.trim().toLowerCase());
    // Signed in when they press "Start creating" — signing in now would leave this page before the new key shows.
    setDone({ isNew: r.data.isNewSignup, key: r.data.licenseKey, onboarding: r.data.onboardingCompleted, token: r.data.token, user: r.data.user });
    setStep('done');
  };
  const sso = async (p) => { setBusy(true); const e = await startSso(p, { licenseId: init.licenseId, signupToken: init.signupToken }); setBusy(false); if (e) setMsg({ err: true, text: e }); };

  if (step === 'done') return <SignupDone done={done} email={f.email} onGo={() => { signIn(done.token, done.user); navigate(done && done.onboarding === false ? '/onboarding' : '/', { replace: true }); }} />;
  return (
    <>
      <h1 className="bv-signin__title">{step === 'profile' ? t('auth.su.title') : step === 'method' ? t('auth.su.verifyTitle') : t('auth.su.codeTitle')}</h1>
      <p className="bv-signin__sub">{step === 'profile' ? t('auth.su.sub') : step === 'method' ? t('auth.su.confirm', { email: f.email.trim() }) : t('auth.su.codeSub')}</p>
      {step === 'profile' && (
        <p className="bv-signin__warn" data-testid="signup-orgwarn"><Trans t={t} i18nKey="auth.su.orgWarn" components={{ back: <button type="button" onClick={() => go('printer')} data-testid="signup-orgwarn-link" /> }} /></p>
      )}
      {step === 'profile' && (
        <form onSubmit={next} noValidate className="bv-signup" data-testid="signup-profile">
          <label className="bv-label" htmlFor="su-name">{t('auth.su.fullName')}</label>
          <Input id="su-name" autoComplete="name" placeholder={t('auth.su.namePlaceholder')} value={f.name} onChange={set('name')} autoFocus />
          <label className="bv-label" htmlFor="su-email">{t('auth.email')}</label>
          <Input id="su-email" type="email" autoComplete="email" placeholder="you@school.org" value={f.email} onChange={set('email')} />
          <span className="bv-label">{t('auth.su.accountType')}</span>
          <div className="bv-seg2" role="radiogroup" aria-label={t('auth.su.accountType')}>
            {[['personal', t('auth.su.personal')], ['business', t('auth.su.business')]].map(([v, l]) => (
              <button key={v} type="button" role="radio" aria-checked={f.account_type === v} className={f.account_type === v ? 'is-on' : ''} onClick={() => setF((x) => ({ ...x, account_type: v }))}>{l}</button>
            ))}
          </div>
          {f.account_type === 'business' && (<><label className="bv-label" htmlFor="su-org">{t('auth.su.orgName')}</label><Input id="su-org" placeholder={t('auth.su.orgPlaceholder')} value={f.organization_name} onChange={set('organization_name')} /></>)}
          <span className="bv-label">{t('auth.su.address')}</span>
          <Input aria-label={t('auth.su.street')} placeholder={t('auth.su.street')} autoComplete="street-address" value={f.address_street} onChange={set('address_street')} />
          <div className="bv-signup__row">
            <Input aria-label={t('auth.su.city')} placeholder={t('auth.su.city')} autoComplete="address-level2" value={f.address_city} onChange={set('address_city')} />
            <Input aria-label={t('auth.su.region')} placeholder={t('auth.su.region')} autoComplete="address-level1" value={f.address_region} onChange={set('address_region')} />
          </div>
          <div className="bv-signup__row">
            <Input aria-label={t('auth.su.postal')} placeholder={t('auth.su.postal')} autoComplete="postal-code" value={f.address_postal_code} onChange={set('address_postal_code')} />
            <Select aria-label={t('auth.su.country')} value={f.address_country} onChange={set('address_country')} options={COUNTRY_OPTIONS.map((c) => ({ value: c.code, label: c.name }))} />
          </div>
          <Button type="submit" variant="coral" block disabled={busy} style={{ marginTop: 12, height: 52 }}>{busy ? t('common.working') : t('common.continue')}</Button>
        </form>
      )}
      {step === 'method' && (
        <div data-testid="signup-method">
          <Button variant="coral" block icon="arrow" disabled={busy} style={{ height: 52 }} onClick={sendCode}>{busy ? t('print.card.sending') : t('auth.su.emailMeSix')}</Button>
          <SsoRow providers={providers} label={t('auth.su.orSignUpWith')} onPick={sso} busy={busy} />
          <p className="bv-small bv-muted bv-center">{t('auth.su.useEmail', { email: f.email.trim() })}</p>
          <div className="bv-signin__alt"><button type="button" onClick={() => setStep('profile')}>{t('auth.su.backDetails')}</button></div>
        </div>
      )}
      {step === 'otp' && (
        <form onSubmit={verify} noValidate data-testid="signup-code">
          <label className="bv-label" htmlFor="su-code">{t('auth.sixDigit')}</label>
          <Input id="su-code" className="bv-otp" inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="000000" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} autoFocus />
          <Button type="submit" variant="coral" block disabled={busy} style={{ marginTop: 10, height: 52 }}>{busy ? t('auth.su.verifying') : t('auth.su.verify')}</Button>
          <div className="bv-signin__alt"><button type="button" onClick={sendCode}>{t('auth.resend')}</button>{' · '}<button type="button" onClick={() => setStep('method')}>{t('auth.su.differentMethod')}</button></div>
        </form>
      )}
      {msg && <p className={`bv-signin__msg ${msg.err ? 'bv-signin__msg--err' : 'bv-signin__msg--ok'}`} role={msg.err ? 'alert' : 'status'} data-testid="signup-msg">{msg.text}</p>}
      {step === 'profile' && <div className="bv-signin__alt"><button type="button" onClick={() => go('choose')}>{t('common.back')}</button></div>}
    </>
  );
}

export function SignupDone({ done, email, onGo, method }) {
  const { t } = useTranslation('bv');
  return (
    <div className="bv-signup-done" data-testid="signup-done">
      <span className="bv-icontile bv-icontile--green" style={{ margin: '0 auto' }}><Icon name="check" size={30} /></span>
      <h1 className="bv-signin__title">{t('auth.done.title')}</h1>
      {done && done.isNew ? (
        <>
          <p className="bv-signin__sub">{t('auth.done.ready')}</p>
          {done.key && (<div className="bv-licok"><div className="bv-label">{t('auth.done.yourKey')}</div><div className="bv-bigkey" data-testid="new-license-key">{done.key}</div><p className="bv-small bv-muted">{t('auth.done.tied')}</p></div>)}
        </>
      ) : (
        <p className="bv-signin__msg bv-signin__msg--err">{method && method !== 'email'
          ? t('auth.done.mismatch', { method, email: email || t('auth.done.theOne') })
          : t('auth.done.expired')}</p>
      )}
      <Button variant="coral" block style={{ height: 52, marginTop: 12 }} onClick={onGo}>{t('auth.done.start')}</Button>
    </div>
  );
}

export default function SignInPage() {
  const { t } = useTranslation('bv');
  const { signedIn } = useSession();
  const location = useLocation();
  const back = (location.state && location.state.from) || '/';
  const [view, setView] = useState(() => (location.pathname === '/sign-up' ? 'signup' : location.pathname === '/license' ? 'license' : 'choose'));
  const [license, setLicense] = useState(null);
  const [presetEmail, setPresetEmail] = useState(null);
  const [providers, setProviders] = useState(null);
  const [reason] = useState(() => { const r = store.get('bv.logoutReason'); store.del('bv.logoutReason'); return r; });
  useEffect(() => { fetch(`${API_BASE}/auth/providers`).then((r) => r.json()).then(setProviders).catch(() => setProviders(null)); }, []);
  if (signedIn) return <Navigate to={back} replace />;
  return (
    <div className="bv-signin">
      <Card className={`bv-signin__card${view === 'signup' || view === 'request' || view === 'org' || view === 'printer' ? ' is-wide' : ''}`} pad={false}>
        <img className="bv-signin__logo" src="/brand/logo-long-on-light.svg" alt="Brightivity" />
        {view === 'choose' && <Choose go={setView} reason={reason} />}
        {view === 'printer' && <PrinterAsk go={setView} />}
        {view === 'org' && <OrgAsk go={setView} />}
        {view === 'request' && <KeyRequest go={setView} />}
        {view === 'license' && <License go={setView} onValid={(l) => { setLicense(l); setView('signin'); }} />}
        {view === 'signin' && <SignIn go={(v) => { setLicense(null); setView(v); }} license={license} presetEmail={presetEmail} providers={providers} back={back} />}
        {view === 'signup' && <SignUp go={setView} providers={providers} onExists={(em) => { setPresetEmail(em); setView('signin'); }} />}
        <p className="bv-signin__foot">{t('auth.tagline')}</p>
      </Card>
      {!providers && <span hidden><Spinner /></span>}
    </div>
  );
}
